import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

/**
 * Row container for all 3 statistic cards
 */
export const StyledCardsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(10), // 40px from Figma (5 * 8px)
  alignItems: 'stretch',
  width: '100%',

  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

/**
 * Shared card shell - container for all statistic card variants
 * From Figma: background-contrast bg, fifth-text border, 16px radius,
 * padding 8px top / 24px right / 16px bottom / 24px left, 160px total height
 */
export const StyledCardShell = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.contrast,
  border: `1px solid ${theme.palette.text.fifth}`,
  borderRadius: theme.customShape.borderRadiusXLarge, // 16px
  height: 160,
  padding: `${theme.spacing(2)} ${theme.spacing(6)} ${theme.spacing(4)}`, // 8px top, 24px left/right, 16px bottom
  gap: theme.spacing(2), // 8px
  overflow: 'hidden',
  flex: '1 0 0',
  minWidth: 0,
  isolation: 'isolate',

  [theme.breakpoints.down('lg')]: {
    height: 'auto',
    minHeight: 160,
  },
}));

/**
 * Header row: title + info icon on left, optional tag on right
 */
export const StyledHeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: theme.spacing(6), // 24px
  flexShrink: 0,
}));

/**
 * Title container with info icon
 */
export const StyledTitleGroup = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5), // 4px
}));

/**
 * Card title text (12px caption style)
 */
export const StyledCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.06px',
  color: theme.palette.text.second,
  whiteSpace: 'nowrap',
}));

/**
 * Tag badge (green "Balanced" style from Figma)
 */
interface StyledTagProps {
  tagVariant: 'success' | 'warning' | 'error';
}

export const StyledTag = styled(Chip)<StyledTagProps>(({
  theme,
  tagVariant,
}) => {
  const colorMap = {
    success: {
      border: theme.palette.success.accent,
      bg: alpha(theme.palette.success.accent, 0.1),
      text: theme.palette.text.main,
    },
    warning: {
      border: theme.palette.warning.main,
      bg: alpha(theme.palette.warning.main, 0.1),
      text: theme.palette.text.main,
    },
    error: {
      border: theme.palette.error.main,
      bg: alpha(theme.palette.error.main, 0.1),
      text: theme.palette.text.main,
    },
  };

  const colors = colorMap[tagVariant];

  return {
    height: theme.spacing(6),
    padding: theme.spacing(0, 2),
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.07px',
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.bg,
    color: colors.text,
    borderRadius: 6,

    '& .MuiChip-label': {
      padding: 0,
    },
  };
});

// ---- Variant 1: Market Motion ----

/**
 * Content layout for Market Motion card: score left, asset list right
 */
export const StyledMarketMotionContent = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  width: '100%',
  flex: 1,
}));

/**
 * Left score section: large number + percentage
 */
export const StyledScoreSection = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(0.5), // 4px
  alignItems: 'flex-start',
  flex: '1 0 0',
  maxWidth: 80,
}));

/**
 * Large score number (36px italic)
 */
export const StyledScoreValue = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(36),
  fontWeight: 400,
  fontStyle: 'italic',
  lineHeight: 1,
  letterSpacing: '0.09px',
  color: theme.palette.text.main,

  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(24),
  },
}));

/**
 * Score column layout (symbol + percentage)
 */
export const StyledScoreColumn = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.5),
}));

/**
 * Score percentage subtitle (20px)
 */
interface ScorePercentageProps {
  percentageColor?: string;
}

export const StyledScorePercentage = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'percentageColor',
})<ScorePercentageProps>(({ theme, percentageColor }) => ({
  fontSize: theme.typography.pxToRem(20),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.03px',
  color: percentageColor ?? theme.palette.text.second,
}));

/**
 * Right side: asset list
 */
export const StyledAssetList = styled(Stack)(({ theme }) => ({
  flex: '1 0 0',
  gap: theme.spacing(0.5), // 4px
  alignItems: 'flex-end',
}));

/**
 * Single asset row: symbol | icon | win rate — three columns, always aligned.
 */
export const StyledAssetRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  gap: theme.spacing(1), // 8px
  alignItems: 'center',
  width: '100%',
}));

/**
 * Icon + symbol name pair in the asset list row — always side-by-side.
 */
export const StyledAssetIconNameGroup = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

/**
 * Main asset row: icon on top of symbol text between 1200px–1439px, side-by-side everywhere else.
 */
export const StyledMainAssetRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),

  '@media (min-width: 1200px) and (max-width: 1439px)': {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },
}));

/**
 * Asset category text (14px medium)
 */
export const StyledAssetCategory = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.main,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(12),
  },
}));

/**
 * Asset win rate percentage (14px semibold)
 */
export const StyledAssetWinRate = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.main,
  width: 46,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('lg')]: {
    fontSize: theme.typography.pxToRem(12),
  },
}));

// ---- Variant 2: Markets Motion Bars ----

/**
 * Container for the gradient bar columns
 */
export const StyledBarsRow = styled(Stack)(() => ({
  flexDirection: 'row',
  justifyContent: 'space-around',
  alignItems: 'flex-end',
  width: '100%',
  flex: 1,
}));

/**
 * Single bar column (gradient bar + label)
 */
export const StyledBarColumn = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(0.5), // 4px
  height: 102,
}));

/**
 * Gradient bar container
 */
export const StyledGradientBarContainer = styled(Stack)(() => ({
  width: 11,
  flex: 1,
  alignItems: 'flex-start',
  justifyContent: 'center',
  position: 'relative',
}));

/**
 * The actual gradient bar fill
 */
export const StyledGradientBar = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  borderRadius: 8,
  overflow: 'hidden',
  background:
    'linear-gradient(to bottom, #0DE77E 15.7%, #F9C509 48.6%, #FE4B4B 83.5%)',
}));

/**
 * Indicator line on the gradient bar
 */
interface StyledIndicatorLineProps {
  indicatorPosition: number; // 0-100 percentage from top
}

export const StyledIndicatorLine = styled(Box)<StyledIndicatorLineProps>(
  ({ theme, indicatorPosition }) => ({
    position: 'absolute',
    left: '50%',
    top: `${indicatorPosition}%`,
    width: 26.904,
    height: 2,
    backgroundColor: theme.palette.text.second,
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
  }),
);

/**
 * Bar label text (12px caption, centered)
 */
export const StyledBarLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.06px',
  color: theme.palette.text.second,
  textAlign: 'center',
  whiteSpace: 'nowrap',
}));

// ---- Variant 3: Volume Distribution ----

/**
 * Main value row for volume distribution card
 */
export const StyledVolumeMainValue = styled(Stack)(() => ({
  flexDirection: 'row',
  gap: 4,
  alignItems: 'flex-end',
}));

/**
 * Large asset name (36px italic)
 */
export const StyledVolumeAssetName = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(36),
  fontWeight: 400,
  fontStyle: 'italic',
  lineHeight: 1,
  letterSpacing: '0.09px',
  color: theme.palette.text.main,
}));

/**
 * Volume percentage (20px)
 */
export const StyledVolumePercentage = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(20),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.03px',
  color: theme.palette.text.second,
}));

/**
 * Horizontal stacked bar container (labels + bar)
 */
export const StyledStackedBarSection = styled(Stack)(() => ({
  width: '100%',
  overflow: 'hidden',
  paddingTop: 18,
  borderRadius: 8,
}));

/**
 * Labels row above the stacked bar
 */
export const StyledStackedBarLabels = styled(Stack)(() => ({
  flexDirection: 'row',
  width: '100%',
}));

/**
 * Single label above a bar segment
 */
interface StyledSegmentLabelContainerProps {
  widthPercent: number;
}

export const StyledSegmentLabelContainer = styled(
  Stack,
)<StyledSegmentLabelContainerProps>(({ widthPercent, theme }) => ({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  minWidth: 40,

  [theme.breakpoints.up('lg')]: {
    flex: widthPercent,
  },
}));

/**
 * Segment label text (12px caption)
 */
export const StyledSegmentLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.06px',
  color: theme.palette.text.main,
}));

/**
 * The stacked bar row
 */
export const StyledStackedBar = styled(Stack)(() => ({
  flexDirection: 'row',
  width: '100%',
  height: 17,
}));

/**
 * Individual segment in the stacked bar
 */
interface StyledBarSegmentProps {
  widthPercent: number;
  bgColor: string;
  isFirst?: boolean;
  isLast?: boolean;
}

export const StyledBarSegment = styled(Stack)<StyledBarSegmentProps>(
  ({ widthPercent, bgColor, isFirst, isLast, theme }) => ({
    flexDirection: 'row',
    flex: 1,
    minWidth: 40,
    height: '100%',
    backgroundColor: bgColor,
    alignItems: 'center',
    justifyContent: 'center',
    borderTopLeftRadius: isFirst ? 8 : 0,
    borderBottomLeftRadius: isFirst ? 8 : 0,
    borderTopRightRadius: isLast ? 8 : 0,
    borderBottomRightRadius: isLast ? 8 : 0,

    [theme.breakpoints.up('lg')]: {
      flex: widthPercent,
    },
  }),
);

/**
 * Percentage text inside a bar segment (12px medium, white)
 */
export const StyledSegmentPercentage = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 500,
  lineHeight: 1.4,
  letterSpacing: '0.06px',
  color:
    theme.palette.mode === 'light'
      ? theme.palette.common.white
      : theme.palette.common.black,
  padding: '0 4px',
}));
